"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import type { IValueRating } from "../../../shared/models/three-sixty-feedback-models/ValueRating"
import type { ITemplateRating } from "../../../shared/models/three-sixty-feedback-models/TemplateRating"
import ClientResubmissionTable from "../ratings/components/ClientResubmissionTable"
import { RotateCcw, Activity } from "lucide-react"

interface ILeadershipRatings {
  overallRating: number
  id: string
  rateeId: string
  dimension: string
  isflagged: boolean
  department: string
  description: string
  criteria: {
    [raterId: string]: {
      additionalComment?: string
      stopDoing: string
      startDoing: string
      continueDoing: string
      ratings: {
        [criteriaName: string]: {
          [statement: string]: number
        }
      }
      leadershipScore?: number
    }
  }
  date: number
  raterId?: string
  templateId?: string
  heading?: string
  reasonForRequest?: string
  dueDate?: number
  values?: {}
  archived?: boolean
}

const Resubmissions = observer(() => {
  const { api, store } = useAppContext()
  const me = store.auth.meJson?.uid
  const [description, setDescription] = useState("")
  const [loading, setLoading] = useState(true)

  const valueRatingNew = store.valueRating.all
    .filter(
      (value) =>
        value.asJson.raterId === me &&
        value.asJson.isflagged === true &&
        value.asJson.adminApproval === "Approved for Re-rating" &&
        value.asJson.resubmission !== "Closed",
    )
    .map((value) => value.asJson)

  const leadershipRatings = store.leadershipRating.all
    .filter(
      (rating) =>
        rating.asJson.isflagged &&
        rating.asJson.raterId === me &&
        rating.asJson.resubmission !== "Closed" &&
        rating.asJson.isflagged === true &&
        rating.asJson.adminApproval === "Approved for Re-rating",
    )
    .map((l) => l.asJson)

  const templateRatings = store.templateRating.all
    .filter((value) => value.asJson.rateeId === me && value.asJson.status === "Completed")
    .map((value) => value.asJson)

  const mapValueToLeadership = (valueRating: IValueRating): ILeadershipRatings => {
    const mappedLeadershipRating: ILeadershipRatings = {
      id: valueRating.id,
      rateeId: valueRating.rateeId,
      date: valueRating.date,
      dimension: valueRating.dimension,
      criteria: {},
      overallRating: valueRating.overallRating,
      isflagged: valueRating.isflagged,
      department: valueRating.department,
      description: valueRating.description,
    }

    Object.keys(valueRating.values).forEach((raterId) => {
      const criteriaName = Object.keys(valueRating.values[raterId].ratings)[0]
      mappedLeadershipRating.criteria[raterId] = {
        additionalComment: valueRating.values[raterId].additionalComment,
        stopDoing: valueRating.values[raterId].stopDoing,
        startDoing: valueRating.values[raterId].startDoing,
        continueDoing: valueRating.values[raterId].continueDoing,
        ratings: {
          [criteriaName]: valueRating.values[raterId].ratings[criteriaName],
        },
      }
    })

    return mappedLeadershipRating
  }

  const mapTemplateToLeadership = (valueRating: ITemplateRating): ILeadershipRatings => {
    const mappedLeadershipRating: ILeadershipRatings = {
      id: valueRating.id,
      rateeId: valueRating.rateeId,
      date: valueRating.dateRequested ?? 0,
      dimension: valueRating.templateId,
      criteria: {},
      overallRating: 0,
      isflagged: false,
      department: valueRating.department,
      description: valueRating.description,
      raterId: valueRating.raterId,
      templateId: valueRating.templateId,
      heading: valueRating.heading,
      reasonForRequest: valueRating.reasonForRequest,
      dueDate: valueRating.dueDate,
      values: valueRating.values,
      archived: valueRating.archived ?? false,
    }

    Object.keys(valueRating.values).forEach((raterId) => {
      const criteriaName = Object.keys(valueRating.values[raterId].ratings)[0]
      mappedLeadershipRating.criteria[raterId] = {
        additionalComment: valueRating.values[raterId].additionalComment,
        stopDoing: valueRating.values[raterId].stopDoing,
        startDoing: valueRating.values[raterId].startDoing,
        continueDoing: valueRating.values[raterId].continueDoing,
        ratings: {
          [criteriaName]: valueRating.values[raterId].ratings[criteriaName],
        },
      }
    })

    return mappedLeadershipRating
  }

  const valueRatesnew = valueRatingNew.map((valueRatingNew) => {
    return mapValueToLeadership(valueRatingNew)
  })

  const templateRates = templateRatings.map((valueRating) => {
    return mapTemplateToLeadership(valueRating)
  })

  const combinedRatings = [...valueRatesnew, ...leadershipRatings, ...templateRates]

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        await api.user.getAll()
        await api.ratingAssignments.getAll()
        const users = store.user.all.map((user) => user.asJson)
        const uids = users.map((user) => user.uid)
        const descriptions = store.ratingAssignments.all.find((rating) => rating.asJson.isActive === true)?.asJson
          .description
        setDescription(descriptions ?? "")

        if (descriptions) {
          await api.templateRating.getAll()
          await api.ratingAssignments.getAll()
          if (descriptions) {
            await api.valueRating.getAllNew(uids, descriptions)
            await api.leadershipRating.getAllNew(uids, descriptions)
          }
        }
      } catch (error) {
        console.error("Error loading APIs:", error)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [api.valueRating, api.templateRating])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-orange-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <RotateCcw className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Resubmissions</h2>
            <p className="text-slate-600">Ratings that require resubmission</p>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{combinedRatings.length}</div>
            <div className="text-xs text-slate-600">Total Items</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{combinedRatings.filter((r) => r.isflagged).length}</div>
            <div className="text-xs text-slate-600">Flagged</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
        {combinedRatings.length > 0 ? (
          <ClientResubmissionTable data={combinedRatings} description={description} />
        ) : (
          <div className="text-center py-12">
            <div className="p-4 bg-green-100 rounded-full w-fit mx-auto mb-4">
              <RotateCcw className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Resubmissions Required</h3>
            <p className="text-slate-600">All your ratings are up to date and don't require resubmission.</p>
          </div>
        )}
      </div>
    </div>
  )
})

export default Resubmissions
